import React, { useEffect, useState } from "react";
import { CalendarX2, Loader2 } from "lucide-react";
import { consultApi } from "../lib/consult";

export const SlotPicker = ({ doctorId, value, onChange, testPrefix = "slot" }) => {
    const [days, setDays] = useState([]);
    const [day, setDay] = useState(value?.slot_date || "");
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!doctorId) return;
        consultApi.get(`/availability/${doctorId}`).then(({ data }) => {
            const list = data.days || [];
            setDays(list);
            setDay((prev) => prev || list.find((d) => d.slots_open > 0)?.date || "");
        }).catch(() => setDays([]));
    }, [doctorId]);

    useEffect(() => {
        if (!doctorId || !day) return;
        setLoading(true);
        consultApi.get(`/availability/${doctorId}/slots`, { params: { day } })
            .then(({ data }) => setSlots(data.slots || []))
            .catch(() => setSlots([]))
            .finally(() => setLoading(false));
    }, [doctorId, day]);

    const open = days.filter((d) => d.slots_open > 0);

    return (
        <div data-testid={`${testPrefix}-picker`}>
            <div className="mono text-[10px] tracking-[0.22em] text-zinc-500">PICK A DATE</div>
            {open.length === 0 ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-zinc-400">
                    <CalendarX2 className="w-4 h-4 text-gold" /> No slots published yet for this consultant.
                </div>
            ) : (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                    {open.map((d) => (
                        <button key={d.date} onClick={() => setDay(d.date)}
                            data-testid={`${testPrefix}-day-${d.date}`}
                            className={`shrink-0 w-[74px] py-3 border text-center transition-colors ${
                                day === d.date ? "border-gold bg-gold/10 text-gold" : "border-white/12 text-zinc-300 hover:border-white/30"
                            }`}>
                            <div className="mono text-[9px] tracking-[0.18em] opacity-70">{d.day_short.toUpperCase()}</div>
                            <div className="font-display text-2xl leading-none mt-1">{d.day_num}</div>
                            <div className="mono text-[9px] tracking-[0.14em] opacity-70 mt-1">{d.month_short.toUpperCase()}</div>
                        </button>
                    ))}
                </div>
            )}

            {day && (
                <>
                    <div className="mono text-[10px] tracking-[0.22em] text-zinc-500 mt-6">PICK A TIME (IST)</div>
                    {loading ? (
                        <div className="mt-3 flex items-center gap-2 text-sm text-zinc-500">
                            <Loader2 className="w-4 h-4 animate-spin" /> Checking availability…
                        </div>
                    ) : slots.length === 0 ? (
                        <div className="mt-3 text-sm text-zinc-400">All slots for this date are taken. Please pick another date.</div>
                    ) : (
                        <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {slots.map((s) => {
                                const on = value?.slot_date === day && value?.slot_time === s.time;
                                return (
                                    <button key={s.time} onClick={() => onChange({ slot_date: day, slot_time: s.time, label: s.label })}
                                        data-testid={`${testPrefix}-time-${s.time}`}
                                        className={`py-2.5 border text-xs transition-colors ${
                                            on ? "border-gold bg-gold/10 text-gold" : "border-white/12 text-zinc-300 hover:border-white/30"
                                        }`}>
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
